<!-- src/shared/components/layout/MastHead.vue -->

<script setup lang="ts">
  import DefaultLogo from '@/shared/components/logos/DefaultLogo.vue';
  import UserMenu from '@/shared/components/navigation/UserMenu.vue';
  import { useHeaderEnabled } from '@/shared/composables/useHeaderEnabled';
  import { DEFAULT_LOGO_COMPONENT } from '@/shared/constants/brand';
  import { useAuthStore } from '@/shared/stores/authStore';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { useProductIdentity } from '@/shared/stores/identityStore';
  import type { LayoutProps } from '@/types/ui/layouts';
  import { storeToRefs } from 'pinia';
  import { computed, watch, type Component, onMounted, shallowRef } from 'vue';
  import { useI18n } from 'vue-i18n';

  const authStore = useAuthStore();
  const bootstrapStore = useBootstrapStore();
  const {
    authentication,
    awaiting_mfa,
    email,
    cust,
    ui,
  } = storeToRefs(bootstrapStore);

  // Brand identity resolves through the central resolver so the masthead shares
  // one neutral-safe source of truth with every other surface — the header reads
  // no raw brand/identity bootstrap fields directly. `productName` replaces the
  // hand-rolled `brand_product_name ?? NEUTRAL` snippet; `showPlatformIdentity`
  // is the base "may we show the platform wordmark?" decision (custom domains /
  // per-tenant logos suppress it — the A3 leak); `logoSource` supplies the
  // logo image on the identity axis: tenant logo, else the operator's
  // install-wide brand.logo_url (BRAND_LOGO_URL, suppressed on custom
  // domains), else the neutral DefaultLogo sentinel (#3612 — the operator
  // logo no longer comes from ui.header.branding).
  const identityStore = useProductIdentity();
  const {
    productName,
    displayName,
    showPlatformIdentity,
    installLogoUri,
    installLogoAlt,
    logoSource,
  } = storeToRefs(identityStore);

  const props = withDefaults(defineProps<LayoutProps>(), {
    displayMasthead: true,
    displayNavigation: true,
    colonel: false,
  });

  const isColonel = computed(() => cust.value?.role === 'colonel');

  // User is partially or fully authenticated - use centralized auth store getter
  // Partially: email verified but awaiting MFA (awaiting_mfa = true, has email but no cust)
  // Fully: all authentication steps complete (authenticated = true, has cust)
  const isUserPresent = computed(() => authStore.isUserPresent);

  // i18n setup
  const { t } = useI18n();

  // Header configuration
  const headerConfig = computed(() => ui.value?.header);

  // Default logo component for fallback (shared sentinel, also used by the
  // resolver's logoSource so the dynamic-import comparisons below stay in sync).
  const DEFAULT_LOGO = DEFAULT_LOGO_COMPONENT;

  // Helper functions for logo configuration. These are plain functions whose
  // reactivity comes solely from being called inside the logoConfig computed
  // below — they must only close over refs/computeds (props, resolver refs,
  // headerConfig), never a captured plain value, or updates stop propagating.
  // Priority: props.logo.url (caller) > identity.logoSource, which itself
  // resolves tenant logo > operator brand.logo_url (BRAND_LOGO_URL, custom
  // domains excepted) > neutral DefaultLogo terminal. The masthead no longer
  // holds an operator-logo rung of its own — config authority lives in the
  // brand: block and resolution in the identity resolver (#3612).
  const getLogoUrl = () => props.logo?.url || logoSource.value;
  // Alt text: caller > operator BRAND_LOGO_ALT (only while the install logo
  // is the asset being shown) > i18n string derived from the product name.
  // When platform identity is suppressed (custom domain / tenant logo), the
  // accessible name must not leak the operator's product name either — use
  // the tenant-facing displayName (brand description or display domain),
  // mirroring what BrandedMastHead does for the same surface.
  const getLogoAlt = () =>
    props.logo?.alt ||
    installLogoAlt.value ||
    (showPlatformIdentity.value
      ? t('web.homepage.one_time_secret_literal', { product_name: productName.value })
      : displayName.value);
  const getLogoHref = () => props.logo?.href || headerConfig.value?.logo?.href || '/';

  // Custom install-wide logo: operator set BRAND_LOGO_URL. Distinct from the
  // tenant logo (identity.logoUri), because the two have different override
  // semantics for the site name. Sentinel comparison is gone: component
  // sentinels can never enter brand.logo_url (Config#normalize_brand rejects
  // them), so presence alone means "operator customized the logo".
  const isCustomStaticLogo = computed(() => !!installLogoUri.value);

  // LOGO_PROMINENT opt-in for larger logo sizing.
  const isProminentLogo = computed(() =>
    headerConfig.value?.logo?.prominent === true
  );

  // Logo sizing: LOGO_PROMINENT controls size, auth state determines the tier.
  // Default (prominent=false): 48px unauthenticated, 40px authenticated
  // Prominent (prominent=true): 160px unauthenticated, 80px authenticated
  const getLogoSize = () => {
    if (props.logo?.size) return props.logo.size;
    if (isProminentLogo.value) return isUserPresent.value ? 80 : 160;
    return isUserPresent.value ? 40 : 48;
  };
  // Priority:
  //   1. props.logo.showSiteName            (caller-site override)
  //   2. !identity.showPlatformIdentity     (resolver base guard: a per-tenant
  //                                          logo or any custom domain suppresses
  //                                          the platform wordmark — the A3 leak)
  //   3. headerConfig.logo.show_name        (LOGO_SHOW_NAME explicit layout knob;
  //                                          unset ships as null so the heuristic
  //                                          below can act)
  //   4. isCustomStaticLogo.value           (heuristic: a custom BRAND_LOGO_URL
  //                                          usually embeds its own wordmark)
  //   5. true                               (default: show the resolver-supplied
  //                                          product name next to the neutral mark)
  const getShowSiteName = () => {
    if (props.logo?.showSiteName != null) return props.logo.showSiteName;
    if (!showPlatformIdentity.value) return false;

    const showName = headerConfig.value?.logo?.show_name;
    if (showName != null) return showName;

    return !isCustomStaticLogo.value;
  };
  // The wordmark text is the resolver's productName (brand.product_name or
  // the neutral default) — the deprecated header.branding.site_name is
  // absorbed into brand.product_name by Config#normalize_brand (#3612).
  const getSiteName = () => props.logo?.siteName || t('web.homepage.one_time_secret_literal', { product_name: productName.value });
  const getAriaLabel = () => props.logo?.ariaLabel;
  const getIsColonelArea = () => props.logo?.isColonelArea ?? props.colonel;

  // Helper function to get logo configuration
  const getLogoConfig = () => ({
    url: getLogoUrl(),
    alt: getLogoAlt(),
    href: getLogoHref(),
    size: getLogoSize(),
    showSiteName: getShowSiteName(),
    siteName: getSiteName(),
    ariaLabel: getAriaLabel(),
    isColonelArea: getIsColonelArea(),
  });

  // Simplified logo configuration with prop override support
  const logoConfig = computed(getLogoConfig);

  // seuid.com.br wordmark (Figma Make): lowercase mono with the "id" tail in
  // brand blue. Only our own product name gets the two-tone treatment —
  // tenant/custom names keep the plain wordmark rendering.
  const isSeuidWordmark = computed(
    () => logoConfig.value.siteName?.trim().toLowerCase() === 'seuid'
  );

  // seuid.com.br nav quick links. "Docs" and "Status" are NO LONGER derived
  // from the external DOCS_URL/STATUS_URL (footerLinksConfig) — those defaults
  // point at upstream domains that don't exist for this deployment. Both are now
  // INTERNAL router-link routes (/docs, /status; see the router-links below),
  // which always exist. Each is suppressed with the platform identity so a
  // custom domain keeps its own chrome (no SeuID docs/status leaking onto a
  // tenant surface).
  const showDocsLink = computed(() => showPlatformIdentity.value);
  const showStatusLink = computed(() => showPlatformIdentity.value);

  // When a caller passes an explicit pixel size via props.logo.size, we must NOT
  // apply a Tailwind h-* class (the class would override the pixel value). In that
  // case we render the height via an inline style and skip the responsive class.
  const hasExplicitImgSize = computed(() => typeof props.logo?.size === 'number' && props.logo.size > 0);

  // Tailwind height classes mirror getLogoSize() logic.
  // Prominent: h-20 (80px) authenticated, h-24/sm:h-40 (96px mobile, 160px desktop) unauthenticated
  // Default: h-10 (40px) authenticated, h-12 (48px) unauthenticated
  const imgHeightClass = computed(() => {
    if (hasExplicitImgSize.value) return null;
    if (isProminentLogo.value) return isUserPresent.value ? 'h-20' : 'h-24 sm:h-40';
    return isUserPresent.value ? 'h-10' : 'h-12';
  });

  const imgInlineStyle = computed(() =>
    hasExplicitImgSize.value ? { height: `${props.logo!.size}px` } : undefined
  );

  // Operator-level header/navigation gates (HEADER_ENABLED), shared with the
  // header wrappers via the composable. Local headerConfig above stays for
  // branding/logo resolution.
  const { headerEnabled, navigationEnabled } = useHeaderEnabled();

  // Logo component handling. A `.vue`-suffixed URL can only be the neutral
  // DEFAULT_LOGO_COMPONENT sentinel from logoSource, or a caller prop —
  // Config#normalize_brand rejects component references in brand.logo_url,
  // so config-supplied values are always real asset URLs.
  const isVueComponent = computed(() => logoConfig.value.url.endsWith('.vue'));
  const logoComponent = shallowRef<Component | null>(
    isVueComponent.value && logoConfig.value.url === DEFAULT_LOGO
      ? DefaultLogo
      : null
  );

  // Helper function to load logo component
  const loadLogoComponent = async (logoUrl: string) => {
    if (!logoUrl.endsWith('.vue')) {
      logoComponent.value = null;
      return;
    }

    const componentName = logoUrl.replace('.vue', '');
    try {
      const module = await import(`@/shared/components/logos/${componentName}.vue`);
      logoComponent.value = module.default;
    } catch (error) {
      console.warn(`Failed to load logo component: ${logoUrl}`, error);
      await loadDefaultLogo(logoUrl);
    }
  };

  // Helper function to load default logo as fallback
  const loadDefaultLogo = async (originalUrl: string) => {
    if (originalUrl === DEFAULT_LOGO) {
      logoComponent.value = null;
      return;
    }

    try {
      const defaultComponent = DEFAULT_LOGO.replace('.vue', '');
      const module = await import(`@/shared/components/logos/${defaultComponent}.vue`);
      logoComponent.value = module.default;
      console.info(`Loaded fallback logo: ${defaultComponent}`);
    } catch (fallbackError) {
      console.error(`Failed to load fallback logo: ${DEFAULT_LOGO}`, fallbackError);
      logoComponent.value = null;
    }
  };

  // Watch for changes to logoUrl and load Vue component if needed
  watch(() => logoConfig.value.url, loadLogoComponent, { immediate: true });

  // Refresh bootstrap state to ensure auth status is up to date
  onMounted(async () => {
    try {
      await bootstrapStore.refresh();
    } catch (error) {
      console.warn('Failed to refresh bootstrap state:', error);
    }
  });

</script>

<template>
  <div v-if="headerEnabled" class="w-full">
    <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
      <!-- Logo lockup -->
      <div class="shrink-0">
        <div v-if="isVueComponent">
          <component
            id="logo"
            :is="logoComponent"
            v-if="logoComponent"
            v-bind="logoConfig"
            :is-user-present="isUserPresent"
            class="transition-transform" />
        </div>
        <div v-else>
          <a
            :href="logoConfig.href"
            data-testid="header-logo-link"
            class="flex items-center gap-3"
            :aria-label="logoConfig.alt">
            <img
              id="logo"
              :src="logoConfig.url"
              class="w-auto object-contain transition-transform"
              :class="imgHeightClass"
              :style="imgInlineStyle"
              :height="logoConfig.size"
              :alt="logoConfig.alt" />
            <span
              v-if="logoConfig.showSiteName && isSeuidWordmark"
              class="font-mono text-base leading-tight font-bold tracking-[-0.01em] text-day-ink dark:text-night-ink">
              seu<span class="text-brand-500">id</span>
            </span>
            <span
              v-else-if="logoConfig.showSiteName"
              class="font-brand text-lg leading-tight font-bold">
              {{ logoConfig.siteName }}
            </span>
          </a>
        </div>
      </div>

      <!-- Context Switchers slot (collapses progressively: org text at lg+, domain text at md+) -->
      <div v-if="isUserPresent" class="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
        <slot name="context-switchers"></slot>
      </div>

      <!-- Navigation / User Menu -->
      <nav
        v-if="displayNavigation && navigationEnabled"
        role="navigation"
        :aria-label="t('web.layout.main_navigation')"
        class="ml-auto flex shrink-0 items-center justify-end gap-4">
        <!--
          Docs · API · Status quick links (desktop only, Figma Make nav): a
          continuous strip — no gap, a hairline divider before each link,
          filling the bar height so they read as a single unit next to the
          signin button.
        -->
        <div
          v-if="showDocsLink || showStatusLink"
          class="hidden items-stretch self-stretch md:flex">
          <!-- Internal on-site API reference (router-link, always available) -->
          <!-- prettier-ignore-attribute class -->
          <router-link
            v-if="showDocsLink"
            to="/docs"
            data-testid="header-docs-link"
            class="flex items-center border-l border-day-line px-4 font-mono text-[11px]
              tracking-[0.06em] text-day-ink-faint uppercase transition-colors duration-150
              hover:text-day-ink dark:border-white/[0.07] dark:text-night-ink-faint
              dark:hover:text-night-ink">
            {{ t('web.footer.docs') }}
          </router-link>
          <!-- Internal on-site status page (router-link, always available) -->
          <!-- prettier-ignore-attribute class -->
          <router-link
            v-if="showStatusLink"
            to="/status"
            data-testid="header-status-link"
            class="flex items-center border-l border-day-line px-4 font-mono text-[11px]
              tracking-[0.06em] text-day-ink-faint uppercase transition-colors duration-150
              hover:text-day-ink dark:border-white/[0.07] dark:text-night-ink-faint
              dark:hover:text-night-ink">
            {{ t('web.COMMON.status') }}
          </router-link>
        </div>

        <template v-if="isUserPresent">
          <!-- User Menu Dropdown -->
          <UserMenu
            :cust="cust"
            :email="email"
            :colonel="isColonel"
            :awaiting-mfa="awaiting_mfa" />
        </template>

        <template v-else>
          <template v-if="authentication?.enabled">
            <!--
              seuid.com.br nav (Figma Make): quiet mono uppercase link for
              signup; signin is the solid brand button with the lock mark.
              brand-600 (not 500) keeps the 11px white label at 4.5:1+.
            -->
            <!-- prettier-ignore-attribute class -->
            <router-link
              v-if="authentication?.signup"
              to="/signup"
              :title="t('web.homepage.signup_individual_and_business_plans')"
              class="font-mono text-[11px] tracking-[0.06em] text-day-ink-faint
                uppercase transition-colors duration-150
                hover:text-day-ink dark:text-night-ink-faint dark:hover:text-night-ink"
              data-testid="header-signup-cta">
              {{ t('web.COMMON.header_create_account') }}
            </router-link>
            <!-- prettier-ignore-attribute class -->
            <router-link
              v-if="authentication?.signin"
              to="/signin"
              :title="t('web.homepage.log_in_to_onetime_secret', { product_name: productName })"
              data-testid="header-signin-link"
              class="inline-flex items-center gap-1.5 bg-brand-600 px-4 py-2
                font-mono text-[11px] tracking-[0.06em] text-white uppercase
                transition-colors duration-150 hover:bg-brand-700
                focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:outline-none
                dark:focus:ring-offset-night">
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true">
                <rect
                  x="3"
                  y="11"
                  width="18"
                  height="11"
                  rx="1" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              {{ t('web.COMMON.header_sign_in') }}
            </router-link>
          </template>
        </template>
      </nav>
    </div>
  </div>
</template>
